import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, MessageSquare, TrendingUp, TrendingDown, Minus, Plus, Trash2, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

type Tab = 'needs' | 'recent' | 'outcomes';

interface NeedsRow {
  driverId: string;
  driverName: string | null;
  driverCode: string | null;
  compositeScore: number | null;
  scoreConsumption: number | null;
  scoreIdle: number | null;
  scoreDrivingStyle: number | null;
  scoreSchedule: number | null;
  scoreMaintenance: number | null;
  wasteCostSar: number;
  totalTrips: number;
  daysSinceLastCoaching: number | null;
}

interface EventRow {
  id: string;
  driverId: string;
  driverName: string | null;
  driverCode: string | null;
  coachedAt: string;
  coachedByName: string | null;
  reason: string;
  notes: string | null;
  scoreBefore: number | null;
  scoreAfter30d: number | null;
  followUpStatus: string;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const FOLLOW_UP_META: Record<string, { label: string; icon: React.ElementType; tone: string }> = {
  pending:    { label: 'Pending',    icon: Loader2,    tone: 'bg-muted text-muted-foreground' },
  improved:   { label: 'Improved',   icon: TrendingUp, tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  no_change:  { label: 'No change',  icon: Minus,      tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  worse:      { label: 'Worse',      icon: TrendingDown, tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' },
};

export default function CoachingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [tab, setTab] = useState<Tab>('needs');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needs, setNeeds] = useState<NeedsRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [recordOpen, setRecordOpen] = useState<NeedsRow | null>(null);
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<{ tab: Tab; rows: NeedsRow[] | EventRow[] }>('/api/v1/driver-coaching/events?tab=' + tab + '&limit=200');
      if (r.data.tab === 'needs') {
        setNeeds(r.data.rows as NeedsRow[]);
        setEvents([]);
      } else {
        setEvents(r.data.rows as EventRow[]);
        setNeeds([]);
      }
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  async function runAlertsAndOutcomes() {
    setRunning(true);
    try {
      await apiClient.post('/api/v1/driver-coaching/alerts/run');
      await apiClient.post('/api/v1/driver-coaching/outcomes/backfill');
      await refresh();
    } catch (err) {
      window.alert('Run failed: ' + extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  async function deleteEvent(id: string) {
    if (!window.confirm('Delete this coaching event?')) return;
    try {
      await apiClient.delete('/api/v1/driver-coaching/events/' + id);
      await refresh();
    } catch (err) {
      window.alert('Delete failed: ' + extractErrorMessage(err));
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Coaching</div>
          {isAdmin ? (
            <Button variant="ghost" size="sm" className="gap-2 h-8" onClick={runAlertsAndOutcomes} disabled={running}>
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Run alerts
            </Button>
          ) : <div className="w-24" />}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        <div className="flex gap-2 border-b border-border">
          {(['needs', 'recent', 'outcomes'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'px-4 py-2 text-sm capitalize border-b-2 transition-colors -mb-px ' + (tab === t ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {t === 'needs' ? 'Needs coaching' : t === 'recent' ? 'Recent' : 'Outcomes'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : tab === 'needs' ? (
          needs.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
              No drivers need coaching right now. {isAdmin && '(Score ≥ 60 or recently coached.)'}
            </div>
          ) : (
            <div className="space-y-2">
              {needs.map((d) => (
                <div key={d.driverId} className="rounded-lg border border-border p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={'/dashboard/drivers/' + d.driverId + '/performance'} className="font-medium hover:underline">
                        {d.driverName ?? d.driverCode ?? d.driverId.slice(0, 8)}
                      </Link>
                      <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + tierTone(d.compositeScore)}>
                        Score {d.compositeScore !== null ? d.compositeScore.toFixed(0) : '—'}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{d.totalTrips} trips · last 30d</span>
                      {d.wasteCostSar > 0 && <span className="text-amber-700 dark:text-amber-400">{fmtSar(d.wasteCostSar)} waste</span>}
                      <span>{d.daysSinceLastCoaching === null ? 'Never coached' : d.daysSinceLastCoaching + 'd since last'}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button size="sm" className="gap-2" onClick={() => setRecordOpen(d)}>
                      <Plus className="h-4 w-4" /> Record
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          // recent + outcomes
          events.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
              {tab === 'outcomes' ? 'No coaching events past their 30-day mark yet.' : 'No coaching events recorded yet.'}
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((e) => {
                const meta = FOLLOW_UP_META[e.followUpStatus] ?? FOLLOW_UP_META.pending;
                const Icon = meta.icon;
                const delta = e.scoreBefore !== null && e.scoreAfter30d !== null ? e.scoreAfter30d - e.scoreBefore : null;
                return (
                  <div key={e.id} className="rounded-lg border border-border p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Link to={'/dashboard/drivers/' + e.driverId + '/performance'} className="font-medium hover:underline">
                          {e.driverName ?? e.driverCode ?? e.driverId.slice(0, 8)}
                        </Link>
                        <span className="text-xs text-muted-foreground">· {fmtDate(e.coachedAt)}</span>
                        {e.coachedByName && <span className="text-xs text-muted-foreground">· by {e.coachedByName}</span>}
                      </div>
                      <span className={'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + meta.tone}>
                        <Icon className={'h-3 w-3' + (e.followUpStatus === 'pending' ? ' animate-spin' : '')} /> {meta.label}
                      </span>
                    </div>
                    <div className="text-sm mt-1.5">{e.reason}</div>
                    {e.notes && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{e.notes}</div>}
                    <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                      {e.scoreBefore !== null && <span>Before: <span className="font-mono">{e.scoreBefore.toFixed(0)}</span></span>}
                      {e.scoreAfter30d !== null && <span>After 30d: <span className="font-mono">{e.scoreAfter30d.toFixed(0)}</span></span>}
                      {delta !== null && (
                        <span className={delta > 0 ? 'text-emerald-700 dark:text-emerald-400' : delta < 0 ? 'text-rose-700 dark:text-rose-400' : ''}>
                          Δ {delta > 0 ? '+' : ''}{delta.toFixed(0)}
                        </span>
                      )}
                      {isAdmin && (
                        <button onClick={() => deleteEvent(e.id)} className="ms-auto text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 inline-flex items-center gap-1">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </main>

      {recordOpen && (
        <RecordSheet driver={recordOpen} onClose={() => setRecordOpen(null)} onSaved={() => { setRecordOpen(null); refresh(); }} />
      )}
    </div>
  );
}

function tierTone(s: number | null): string {
  if (s === null) return 'bg-muted text-muted-foreground';
  if (s >= 60) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  if (s >= 40) return 'bg-orange-500/15 text-orange-700 dark:text-orange-400';
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
}

function RecordSheet({ driver, onClose, onSaved }: { driver: NeedsRow; onClose: () => void; onSaved: () => void }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (reason.trim().length < 3) { setError('Reason is required'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/driver-coaching/events', {
        driverId: driver.driverId,
        reason: reason.trim(),
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Record coaching
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="rounded-md bg-muted p-3 text-sm">
            <div className="font-medium">{driver.driverName ?? driver.driverCode ?? driver.driverId.slice(0, 8)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Composite score {driver.compositeScore !== null ? driver.compositeScore.toFixed(0) : '—'}
              {driver.wasteCostSar > 0 && ' · ' + fmtSar(driver.wasteCostSar) + ' waste last 30d'}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason</Label>
            <Input id="reason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. excessive idle on Jeddah route" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span></Label>
            <textarea
              id="notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="What did you discuss? Any commitments?"
            />
          </div>

          <div className="text-xs text-muted-foreground">
            We'll snapshot the current composite as "score before". After 30 days, the worker will record the new score and mark the outcome (Improved / No change / Worse).
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Record
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

